import { LISTED_TX_TYPES, type ReceivedSnapshot } from '@apm/contracts';

/**
 * 최근 1분 동시 처리 요약 (23-A회차, UI 피드백 23).
 *
 * "진행 중 요청"은 스냅샷을 찍은 그 순간에 아직 응답이 안 나간 요청만 보인다. OneWorks의
 * 요청은 대부분 1초 안에 끝나므로 평소에는 늘 0건이고, 화면만 보면 아무 일도 안 일어나는
 * 것처럼 보인다. 그래서 빈 목록 옆에 **최근 1분에 가장 많을 때 몇 건이었는지**와
 * **3초를 넘긴 요청이 몇 건이었는지**를 함께 적는다.
 *
 * 서버는 손대지 않는다. 스냅샷은 저장하지 않으므로(실시간 경로 설계) 화면이 받은 것을
 * 모으는 수밖에 없다. 새로고침하면 다시 쌓기 시작한다 — 초당 추이(`RateTrend`)와 달리
 * 서버 링버퍼에서 되찾지 않는다. 1분치라 되찾을 값이 없다.
 *
 * 실시간 연결(sse)은 세지 않는다. 한 번 붙으면 수십 분 유지돼 "동시 처리"의 뜻이 달라지고,
 * 3초 기준으로는 전부 걸린다. 목록과 같은 다섯 중 넷(`LISTED_TX_TYPES`)만 본다.
 *
 * **서비스를 함께 담는다** (23-A회차 리뷰 7). 같은 구획의 진행 중 목록은 고른 서비스만 보는데
 * 요약이 전 서비스를 합치면 두 숫자의 범위가 달라진다. 세는 쪽에서 같은 조건으로 거른다.
 * 보고가 끊겨도 값이 만료되도록 화면이 시간 tick으로 다시 부른다 — 이 클래스는 `summary(now)`를
 * 부를 때마다 창 밖을 버린다.
 */

/** 이 시간을 넘겨 돌고 있으면 "느린 요청"으로 센다. 목록의 '3초 이상만' 필터와 같은 값이다 */
export const SLOW_MS = 3_000;
/** 되돌아보는 창 */
const WINDOW_MS = 60_000;

export interface ActivitySummary {
  /** 창 안에서 한 순간에 가장 많이 돌던 건수 */
  maxConcurrent: number;
  /** 창 안에서 3초를 넘긴 서로 다른 요청 수 */
  slowCount: number;
  /** 실제로 값을 받은 초 수. 0이면 아직 말할 것이 없다 */
  seconds: number;
}

export class ActivityWindow {
  /**
   * 초(epoch s) → 인스턴스 → 그 순간의 동시 처리 건수와 서비스.
   * 인스턴스별로 따로 두는 이유는 둘이다 — 같은 초의 여러 인스턴스는 더해야 하고,
   * 같은 인스턴스의 스냅샷이 두 번 와도 한 번만 세야 한다
   */
  private readonly concurrent = new Map<
    number,
    Map<string, { service: string; running: number }>
  >();
  /** 3초를 넘긴 요청. 키는 인스턴스+실행 id, 구분자는 이름에 못 들어가는 NUL이다 */
  private readonly slow = new Map<string, { at: number; service: string }>();

  constructor(private readonly windowMs: number = WINDOW_MS) {}

  /**
   * 스냅샷 하나를 더한다. 창 밖이면 거짓.
   *
   * 같은 초에 같은 인스턴스의 스냅샷이 두 번 오면 큰 쪽을 남긴다. 덮으면 앞의 값을 잃고
   * 더하면 두 배가 된다 — "가장 많을 때"를 보는 값이라 큰 쪽이 맞다.
   */
  add(snapshot: ReceivedSnapshot, now: number = Date.now()): boolean {
    if (snapshot.ts < now - this.windowMs) return false;
    const second = Math.floor(snapshot.ts / 1_000);
    let running = 0;
    for (const type of LISTED_TX_TYPES) {
      const rows = snapshot.activeTx[type];
      running += rows.length;
      for (const tx of rows) {
        if (tx.elapsedMs >= SLOW_MS) {
          this.slow.set(`${snapshot.instance}\u0000${tx.txId}`, {
            at: now,
            service: snapshot.service,
          });
        }
      }
    }
    let slots = this.concurrent.get(second);
    if (slots === undefined) {
      slots = new Map();
      this.concurrent.set(second, slots);
    }
    slots.set(snapshot.instance, {
      service: snapshot.service,
      running: Math.max(slots.get(snapshot.instance)?.running ?? 0, running),
    });
    this.prune(now);
    return true;
  }

  private prune(now: number): void {
    const cutoff = Math.floor((now - this.windowMs) / 1_000);
    for (const second of this.concurrent.keys()) {
      if (second < cutoff) this.concurrent.delete(second);
    }
    for (const [key, seen] of this.slow) {
      if (seen.at < now - this.windowMs) this.slow.delete(key);
    }
  }

  /**
   * 창 안의 요약. 서비스를 주면 그 서비스만 센다 (23-A회차 리뷰 7).
   * 부를 때마다 창 밖을 버리므로, 화면이 시간 tick으로 다시 부르면 보고가 끊겨도 값이 만료된다.
   */
  summary(now: number = Date.now(), service?: string): ActivitySummary {
    this.prune(now);
    const mine = (name: string) => service === undefined || service === '' || name === service;
    let maxConcurrent = 0;
    let seconds = 0;
    for (const slots of this.concurrent.values()) {
      let running = 0;
      let counted = false;
      for (const value of slots.values()) {
        if (!mine(value.service)) continue;
        running += value.running;
        counted = true;
      }
      // 그 서비스의 보고가 없던 초는 "값을 받은 초"로 세지 않는다
      if (counted) seconds += 1;
      if (running > maxConcurrent) maxConcurrent = running;
    }
    let slowCount = 0;
    for (const seen of this.slow.values()) if (mine(seen.service)) slowCount += 1;
    return { maxConcurrent, slowCount, seconds };
  }
}
